import React from 'react';
import usePhotoCapture from '../hooks/usePhotoCapture';

/**
 * PhotoCapture — drop-in form control for capturing 0…N photos that are
 * stored as base64 data URLs (matching the existing signature / logo
 * upload pattern in this app).
 *
 * Props:
 *   value     : string[]                — current array of base64 dataUrls
 *   onChange  : (next: string[]) => void
 *   limit     : number (default 5)      — max photos
 *   label     : string                  — UI label above the strip
 *   testId    : string (default "photo-capture")
 *   sourcePreference : 'CAMERA' | 'PHOTOS' | 'PROMPT'
 *                                       — 'CAMERA' jumps straight to rear-cam
 *                                         on native; 'PROMPT' (default) lets
 *                                         the user pick "Take photo" or
 *                                         "Choose from gallery".
 *
 * Renders:
 *   • A horizontal strip of thumbnails (each with a tap-to-remove × overlay)
 *   • A "📷 Add Photo" tap-target until the limit is reached
 *
 * Inside the Capacitor native shell the button opens the Camera plugin;
 * in a browser it falls back to a file input that on mobile browsers
 * opens the rear camera (capture="environment").
 */
const PhotoCapture = ({
  value = [],
  onChange,
  limit = 5,
  label = 'Photos',
  testId = 'photo-capture',
  sourcePreference = 'PROMPT',
}) => {
  const { capture } = usePhotoCapture();
  const photos = Array.isArray(value) ? value : [];

  const handleAdd = async () => {
    if (photos.length >= limit) {
      alert(`You can add up to ${limit} photo${limit === 1 ? '' : 's'}.`);
      return;
    }
    try {
      const dataUrl = await capture({ source: sourcePreference });
      if (!dataUrl) return;
      onChange([...photos, dataUrl]);
    } catch (e) {
      // Surface the failure so the user (and the developer) know WHY
      // nothing happened. Silent failures here were the #1 source of
      // "camera not opening" tickets.
      const msg = e?.message || String(e) || 'Unknown error';
      // eslint-disable-next-line no-console
      console.error('[PhotoCapture] capture failed:', e);
      alert(`Couldn't open the camera: ${msg}`);
    }
  };

  const handleRemove = (idx) => {
    onChange(photos.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-baseline justify-between">
        <label className="text-sm font-medium text-slate-700">{label}</label>
        <span className="text-[11px] text-slate-500">
          {photos.length} / {limit}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {photos.map((src, idx) => (
          <div
            key={idx}
            className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-300 bg-slate-50 shadow-sm"
            data-testid={`${testId}-thumb-${idx}`}
          >
            <img
              src={src}
              alt={`Photo ${idx + 1}`}
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={() => handleRemove(idx)}
              aria-label="Remove photo"
              className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-rose-600 text-white text-xs leading-none flex items-center justify-center shadow"
              data-testid={`${testId}-remove-${idx}`}
            >
              ×
            </button>
          </div>
        ))}
        {photos.length < limit && (
          <button
            type="button"
            onClick={handleAdd}
            className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-indigo-500 hover:bg-indigo-50 active:scale-[0.97] transition-all flex flex-col items-center justify-center text-slate-500"
            data-testid={`${testId}-add`}
          >
            <span className="text-2xl">📷</span>
            <span className="text-[10px] mt-0.5 font-medium">Add</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default PhotoCapture;
